import React from 'react';
import { HStack, Text, VStack } from '@chakra-ui/react';
import { LuShield, LuUsers } from 'react-icons/lu';
import { AppSwitch, type AppSwitchOption } from '@/components/ui/app-switch.tsx';

/** String-encoded boolean values for the visibility toggle */
type VisibilityValue = 'true' | 'false';

export const VisibilityCaptionVariant = {
	Upload: 'upload',
	Record: 'record',
	PendingRecord: 'pending-record',
} as const;
export type VisibilityCaptionVariant = (typeof VisibilityCaptionVariant)[keyof typeof VisibilityCaptionVariant];

const VISIBILITY_OPTIONS: [AppSwitchOption<VisibilityValue>, AppSwitchOption<VisibilityValue>] = [
	{ label: 'Off', value: 'false', icon: LuUsers },
	{ label: 'On', value: 'true', icon: LuShield },
];

interface SharingVisibilitySwitchProps {
	showTitle?: boolean;
	layout?: 'stacked' | 'inline';
	titleContent?: React.ReactNode;
	captionVariant?: VisibilityCaptionVariant;
	isPrivate: boolean;
	onValueChange: (isPrivate: boolean) => void;
}

const getCaption = (isPrivate: boolean, variant: VisibilityCaptionVariant) => {
	if (variant === 'pending-record') {
		return isPrivate
			? 'This record will only be available to admins and the owner.'
			: 'This record will be available to everyone with access.';
	}

	if (variant === 'record') {
		return isPrivate
			? 'This record is only available to admins and the owner.'
			: 'This record is available to everyone with access.';
	}

	return isPrivate
		? 'New uploads will only be visible to admins and the owner.'
		: 'New uploads will be visible to everyone with access.';
};

/** Reusable toggle for document privacy (Admins-only vs Everyone) */
const SharingVisibilitySwitch: React.FC<SharingVisibilitySwitchProps> = (props: SharingVisibilitySwitchProps) => {
	const {
		showTitle,
		layout = 'stacked',
		titleContent,
		captionVariant = VisibilityCaptionVariant.Upload,
		isPrivate,
		onValueChange,
	} = props;
	const caption = getCaption(isPrivate, captionVariant);

	const title = showTitle
		? titleContent || (
				<Text textStyle="body.sm" fontWeight="medium" textAlign={layout === 'inline' ? 'left' : 'center'}>
					Record visibility
				</Text>
			)
		: null;

	const control = (
		<AppSwitch
			options={VISIBILITY_OPTIONS}
			value={isPrivate ? 'true' : 'false'}
			onValueChange={(val) => onValueChange(val === 'true')}
			size="md"
			maxW={layout === 'inline' ? { base: '160px', sm: '220px' } : 'full'}
			bg={isPrivate ? 'brand.emphasized' : 'bg.muted'}
		/>
	);

	return (
		<VStack gap={2} align="stretch" w="full">
			{layout === 'inline' ? (
				<HStack justify="space-between" gap={4} w="full">
					{title}
					{control}
				</HStack>
			) : (
				<>
					{title}
					{control}
				</>
			)}
			<Text
				textStyle="micro"
				color="fg.muted"
				textAlign={layout === 'inline' ? 'left' : 'center'}
				lineHeight="1.4"
			>
				{caption}
			</Text>
		</VStack>
	);
};

export default SharingVisibilitySwitch;
